/* eslint-disable no-console */
import React, { useCallback, useEffect, useState } from 'react';
import { HiPlus } from 'react-icons/hi';
import { addHours, format, parseISO } from 'date-fns';

import api from '~/services/api';

import { Container, Filter } from './styles';
import InfiniteScroll from '~/components/InfiniteScroll';
import BtnNewEvent from '~/components/BtnNewEvent';
import ModalNewUser from '~/components/ModalNewUser';
import { IOption } from '~/components/Select';
import FiltersLists from '~/components/FiltersLists';

import imgDefault from '~/assets/icons/image-default.svg';
import Loading from '~/components/Loading';
import Card from './Card';

interface IItem {
  id: number;
}

interface IOrder {
  items: IItem[];
}

interface IBatch {
  id: number;
  quantity: number;
  due_date: string;
}

interface IArchiveEvent {
  archive: {
    archive_url: string;
  };
}

interface IOrganizerUser {
  id: number;
  user: {
    name: string;
  };
}

export interface IEvent {
  priority?: number;
  id: number;
  archive_url: string;
  title: string;
  date: string;
  type: string;
  type_image_url: string;
  status: string;
  unlisted: boolean;
  inactive: boolean;
  endRegistration: string;
  purchases: number;
  allTickets: number;
  total: number;
  avarage: number;
  slug: string;
  organizer: string;
}

interface IEventData {
  organizer_id: number;
  status_name: string;
  qty_total: string;
  qty_sold: string;
  type_archive_url: string;
  type_name: string;
  event_archive_url: string;
  id: number;
  name: string;
  priority?: number;
  description: string;
  date: string;
  slug: string;
  archivesEvents: IArchiveEvent[];
  batches: IBatch[];
  sales_end_date: string;
  unlisted: boolean;
  inactive: boolean;
  orders: IOrder[];
  status: {
    name: string;
  };
  organizer: {
    name: string;
    organizersUsers: IOrganizerUser[];
  };
  type: {
    name: string;
    archive: {
      archive_url: string;
    };
  };
}

interface IEventResponse {
  data: IEventData[];
  from: number;
  to: number;
  total: number;
  pages: number;
}

interface IEventType {
  id: number;
  name: string;
}

interface IStatus {
  id: number;
  name: string;
}

interface IOrganizer {
  id: number;
  name: string;
}

const Events: React.FC = () => {
  const [show, setShow] = useState(false);
  const [events, setEvents] = useState<IEvent[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [categories, setCategories] = useState<IOption[]>([]);
  const [status, setStatus] = useState<IOption[]>([]);
  const [statusSelected, setStatusSelected] = useState<string | undefined>(
    undefined
  );
  const [organizerSelected, setOrganizerSelected] = useState('');
  const [typeSelected, setTypeSelected] = useState<string | undefined>(
    undefined
  );
  const [dateStartSelected, setDateStartSelected] = useState<
    string | undefined
  >(undefined);
  const [dateEndSelected, setDateEndSelected] = useState<string | undefined>(
    undefined
  );
  const [organizers, setOrganizers] = useState<IOption[]>([]);
  const [eventIdData, setEventIdData] = useState<number | undefined>(undefined);
  const [priorityData, setPriorityData] = useState<boolean>(false);

  useEffect(() => {
    api.get<IStatus[]>('events-status').then((response) => {
      const data = response.data.map<IOption>((statusData) => ({
        id: statusData.id,
        value: statusData.name,
        selected: false,
      }));
      setStatus(data);
    });

    api.get<IOrganizer[]>('organizers').then((response) => {
      const dataOrganizers = response.data.map<IOption>((organizer) => ({
        id: organizer.id,
        value: organizer.name,
        selected: false,
      }));
      setOrganizers(dataOrganizers);
    });
  }, []);

  const handleLoadEvents = useCallback(
    async (
      pageData,
      organizer?: string,
      status_id?: string,
      type_id?: string,
      dateStart?: string,
      dateEnd?: string,
      eventId?: number,
      priority?: boolean
    ) => {
      try {
        setLoadingMore(true);
        const response = await api.get<IEventResponse>('events/admin', {
          params: {
            page: pageData,
            organizer_id: organizer,
            category_id: type_id,
            status_ids: status_id ? [status_id] : undefined,
            startDate: dateStart ? new Date(dateStart) : undefined,
            endDate: dateEnd ? new Date(dateEnd) : undefined,
            event_id: eventId,
            priority,
          },
        });

        const data = response.data.data.map<IEvent>((event) => {
          // const allTickets = event.batches.reduce(
          //   (previous, current) => previous + current.quantity,
          //   0
          // );

          // const endRegistration = format(
          //   parseISO(event.sales_end_date || event.date),
          //   'dd/MM/yyyy'
          // );

          // const purchases = event.orders.reduce(
          //   (previous, current) => previous + current.items.length,
          //   0
          // );

          const organizerName = organizers.find(
            (org) => org.id === event.organizer_id
          )?.value;

          return {
            id: event.id,
            archive_url: event.event_archive_url || imgDefault,
            title: event.name,
            priority: event.priority,
            date: format(addHours(parseISO(event.date), 3), 'dd/MM/yyyy'),
            type: event.type_name,
            type_image_url: event.type_archive_url,
            status: event.status_name ?? 'Cadastrando',
            unlisted: event.unlisted,
            inactive: event.inactive || false,
            endRegistration: format(
              parseISO(event.sales_end_date),
              'dd/MM/yyyy'
            ),
            purchases: Number(event.qty_sold),
            allTickets: Number(event.qty_total),
            total: 0,
            avarage: 0,
            slug: event.slug,
            organizer: organizerName || 'organizador',
          };
        });

        if (pageData === 1) {
          setEvents(data);
        } else {
          setEvents((oldState) => [...oldState, ...data]);
        }
        setTotalPages(response.data.pages);
        setLoadingMore(false);
      } catch (error) {
        console.log(error);
      } finally {
        setLoadingMore(false);
      }
    },
    [organizers]
  );

  useEffect(() => {
    handleLoadEvents(1);
  }, [handleLoadEvents]);

  useEffect(() => {
    api
      .get<IEventType[]>('events-types', { params: { isOrganizer: true } })
      .then((response) => {
        const data = response.data.map<IOption>((modality) => ({
          id: modality.id,
          value: modality.name,
          selected: false,
        }));

        setCategories(data);
      });
  }, []);

  const handleShow = useCallback(() => {
    setShow(true);
  }, []);

  const handleLoad = useCallback(async () => {
    if (!loadingMore) {
      setLoadingMore(true);
      if (page < totalPages) {
        await handleLoadEvents(
          page + 1,
          organizerSelected,
          statusSelected !== 'todos' ? statusSelected : undefined,
          typeSelected !== 'todos' ? typeSelected : undefined,
          dateStartSelected,
          dateEndSelected,
          eventIdData,
          priorityData
        );
        setPage(page + 1);
      } else {
        setLoadingMore(false);
      }
    }
  }, [
    loadingMore,
    page,
    totalPages,
    handleLoadEvents,
    organizerSelected,
    statusSelected,
    typeSelected,
    dateStartSelected,
    dateEndSelected,
    eventIdData,
    priorityData,
  ]);

  const handleSubmitFilters = useCallback(
    (data) => {
      setEventIdData(data.eventId ? parseInt(data.eventId, 10) : undefined);
      setStatusSelected(data.status !== 'todos' ? data.status : undefined);
      setOrganizerSelected(
        data.organizer !== 'todos' ? data.organizer : undefined
      );
      setTypeSelected(data.sports !== 'todos' ? data.sports : undefined);
      setDateStartSelected(data.dateStart);
      setDateEndSelected(data.dateEnd);
      setPriorityData(data.priority === 'true');
      handleLoadEvents(
        1,
        data.organizer !== 'todos' ? data.organizer : undefined,
        data.status !== 'todos' ? data.status : undefined,
        data.sports !== 'todos' ? data.sports : undefined,
        data.dateStart,
        data.dateEnd,
        data.eventId ? parseInt(data.eventId, 10) : undefined,
        data.priority === 'true'
      );
      setPage(1);
    },
    [handleLoadEvents]
  );

  return (
    <Container className="mt-n3 mb-n3">
      <InfiniteScroll
        id="infinite-scroll"
        onInfiniteLoad={handleLoad}
        className="infinite-scroll py-3"
      >
        <div className="container-fluid">
          <div className="row">
            <div className="zoom col-12 px-0">
              <div className="bg-white d-flex justify-content-between align-items-center p-4">
                <h1 className="mb-0">Eventos</h1>
                <div>
                  {false && (
                    <>
                      <button
                        type="button"
                        onClick={handleShow}
                        className="mb-0 me-4 h6 fw-normal text-white btn-new px-5 py-2"
                      >
                        <HiPlus size={16} color="#fff" className="me-2" />
                        Novo usuário
                      </button>

                      <BtnNewEvent />
                    </>
                  )}
                </div>
              </div>
            </div>
            <Filter className="col-12 px-0 mt-3">
              <FiltersLists
                onSubmitFilters={handleSubmitFilters}
                organizer
                organizersData={organizers}
                statusEvent
                date
                categories={categories}
                statusOptions={status}
                event
                priority
              />
            </Filter>

            <div className="zoom col-12 pe-0">
              {events.map((event) => (
                <Card key={event.id} event={event} />
              ))}
            </div>
          </div>
        </div>
      </InfiniteScroll>
      {/* <div></div> */}
      <Loading active={loadingMore} text="Carregando..." />
      <ModalNewUser show={show} onClose={() => setShow(false)} />
    </Container>
  );
};

export default Events;
